import { useEffect, useRef, useState } from "react";
import { isSuperAdmin } from "../utils/Util";
import { getMailMessages, getMailCounts, setMailReadState } from "../services/supportMail";
import { IMailListItem, IMailCounts, MailStatus } from "../models/ISupportMail";
import { STATUS_META } from "../components/mail/mailStatusMeta";
import MailDetailPanel from "../components/mail/MailDetailPanel";
import MailFilterRulesModal from "../components/mail/MailFilterRulesModal";

type StatusTab = MailStatus | "All";

const STATUS_TABS: { key: StatusTab; label: string; icon: string; countKey: keyof IMailCounts }[] = [
  { key: "All",           label: "Tümü",             icon: "bi-inbox-fill",              countKey: "all" },
  { key: "TicketCreated", label: "Ticket Açıldı",    icon: "bi-ticket-perforated-fill",  countKey: "ticketCreated" },
  { key: "AddedToTicket", label: "Ticket'a Eklendi", icon: "bi-reply-fill",              countKey: "addedToTicket" },
  { key: "Filtered",      label: "Filtrelendi",      icon: "bi-funnel-fill",             countKey: "filtered" },
  { key: "NotProcessed",  label: "İşlenmedi",        icon: "bi-dash-circle",             countKey: "notProcessed" },
  { key: "Pending",       label: "İşleniyor",        icon: "bi-hourglass-split",         countKey: "pending" },
  { key: "Failed",        label: "Hata",             icon: "bi-exclamation-triangle-fill", countKey: "failed" },
];

function timeAgo(dateStr: string): string {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return "Az önce";
  if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} sa önce`;
  if (diff < 172800) return "Dün";
  return new Date(dateStr).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
}

interface FetchOverrides {
  status?: StatusTab;
  search?: string;
  unreadOnly?: boolean;
  hasAttachment?: boolean;
  dateFrom?: string;
  dateTo?: string;
  pageIndex?: number;
}

export default function MailManagementPage() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const [statusTab, setStatusTab] = useState<StatusTab>("All");
  const [search, setSearch] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [hasAttachment, setHasAttachment] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [pageIndex, setPageIndex] = useState(0);

  const [mails, setMails] = useState<IMailListItem[]>([]);
  const [paging, setPaging] = useState<{ pageIndex: number; totalPages: number; totalCount: number } | null>(null);
  const [counts, setCounts] = useState<IMailCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [showFilterRules, setShowFilterRules] = useState(false);

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const fetchCounts = () => {
    getMailCounts().then((res) => setCounts(res.data)).catch(() => {});
  };

  const fetchMails = (overrides: FetchOverrides = {}) => {
    const params = {
      status: overrides.status ?? statusTab,
      search: (overrides.search ?? search).trim() || undefined,
      unreadOnly: overrides.unreadOnly ?? unreadOnly,
      hasAttachment: overrides.hasAttachment ?? hasAttachment,
      dateFrom: overrides.dateFrom ?? dateFrom,
      dateTo: overrides.dateTo ?? dateTo,
      pageIndex: overrides.pageIndex ?? pageIndex,
    };
    setLoading(true);
    setError("");
    getMailMessages({ ...params, pageSize: 20 })
      .then((res) => {
        setMails(res.data.items ?? []);
        setPaging(res.data.paging ?? null);
      })
      .catch(() => setError("Mailler yüklenemedi."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchMails();
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabClick = (key: StatusTab) => {
    setStatusTab(key);
    setPageIndex(0);
    setSelectedIds(new Set());
    fetchMails({ status: key, pageIndex: 0 });
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPageIndex(0);
      fetchMails({ search: value, pageIndex: 0 });
    }, 400);
  };

  const handleToggleUnread = () => {
    const next = !unreadOnly;
    setUnreadOnly(next);
    setPageIndex(0);
    fetchMails({ unreadOnly: next, pageIndex: 0 });
  };

  const handleToggleAttachment = () => {
    const next = !hasAttachment;
    setHasAttachment(next);
    setPageIndex(0);
    fetchMails({ hasAttachment: next, pageIndex: 0 });
  };

  const handleDateChange = (which: "from" | "to", value: string) => {
    if (which === "from") setDateFrom(value); else setDateTo(value);
    setPageIndex(0);
    fetchMails({ [which === "from" ? "dateFrom" : "dateTo"]: value, pageIndex: 0 });
  };

  const goToPage = (page: number) => {
    setPageIndex(page);
    setSelectedIds(new Set());
    fetchMails({ pageIndex: page });
  };

  const refreshAll = () => {
    fetchMails();
    fetchCounts();
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkSetRead = async (isRead: boolean) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      await setMailReadState(ids, isRead);
      clearSelection();
      refreshAll();
    } catch {
      setError("Toplu işlem başarısız oldu.");
    } finally {
      setBulkBusy(false);
    }
  };

  if (!isSuperAdmin()) {
    return (
      <div className="mail-guard">
        <i className="bi bi-shield-lock-fill" style={{ fontSize: "2.6rem", color: "#dc2626" }}></i>
        <h5 style={{ marginTop: 14, fontWeight: 800, color: "#111827" }}>Erişim Reddedildi</h5>
        <p style={{ color: "#6b7280", fontSize: "0.9rem" }}>Bu sayfayı yalnızca SuperAdmin kullanıcılar görüntüleyebilir.</p>
      </div>
    );
  }

  return (
    <div className="mail-page">
      {/* Header */}
      <div className="mail-header">
        <div className="mail-header__left">
          <div className="mail-header__icon"><i className="bi bi-envelope-fill"></i></div>
          <div style={{ minWidth: 0 }}>
            <h4 className="mail-header__title">Mail Yönetimi</h4>
            <p className="mail-header__subtitle">support@mutfacik.com gelen kutusu</p>
          </div>
        </div>
        <div className="mail-header__actions">
          <button className="mail-icon-btn" onClick={() => setShowFilterRules(true)}>
            <i className="bi bi-funnel-fill"></i> Filtre Kuralları
          </button>
          <button className="mail-icon-btn" onClick={refreshAll} disabled={loading}>
            <i className={`bi bi-arrow-clockwise${loading ? " mail-spin" : ""}`}></i> Yenile
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="mail-toolbar">
        <div className="mail-search-wrap">
          <i className="bi bi-search mail-search-icon"></i>
          <input
            type="text"
            className="mail-search-input"
            placeholder="Gönderen adı, adresi veya konu ara..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
        <div className="mail-filter-row">
          <button className={`mail-chip${unreadOnly ? " mail-chip--active" : ""}`} onClick={handleToggleUnread}>
            <i className="bi bi-envelope-fill"></i> Okunmamış{counts ? ` (${counts.unread})` : ""}
          </button>
          <button className={`mail-chip${hasAttachment ? " mail-chip--active" : ""}`} onClick={handleToggleAttachment}>
            <i className="bi bi-paperclip"></i> Ekli
          </button>
          <input type="date" className="mail-date-input" value={dateFrom} onChange={(e) => handleDateChange("from", e.target.value)} title="Başlangıç" />
          <span style={{ color: "#9ca3af", fontSize: "0.8rem" }}>—</span>
          <input type="date" className="mail-date-input" value={dateTo} onChange={(e) => handleDateChange("to", e.target.value)} title="Bitiş" />
        </div>
      </div>

      {/* Status tabs */}
      <div className="mail-tabs">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            className={`mail-tab-btn${statusTab === tab.key ? " mail-tab-btn--active" : ""}`}
            onClick={() => handleTabClick(tab.key)}
          >
            <i className={`bi ${tab.icon}`}></i>
            {tab.label}
            {counts && <span className="mail-tab-badge">{counts[tab.countKey]}</span>}
          </button>
        ))}
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="mail-bulk-bar">
          <span className="mail-bulk-bar__count">{selectedIds.size} seçili</span>
          <button className="mail-icon-btn" disabled={bulkBusy} onClick={() => bulkSetRead(true)}>
            <i className="bi bi-envelope-open"></i> Okundu İşaretle
          </button>
          <button className="mail-icon-btn" disabled={bulkBusy} onClick={() => bulkSetRead(false)}>
            <i className="bi bi-envelope-fill"></i> Okunmadı İşaretle
          </button>
          <button className="mail-icon-btn" onClick={clearSelection}>Seçimi Temizle</button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 10, background: "#fef2f2", border: "1.5px solid #fecaca", color: "#dc2626", fontSize: "0.85rem", fontWeight: 600 }}>
          <i className="bi bi-exclamation-circle-fill me-2"></i>{error}
        </div>
      )}

      {/* List */}
      {loading && mails.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}><span className="spinner-border text-primary" /></div>
      ) : mails.length === 0 ? (
        <div className="mail-empty">
          <i className="bi bi-inbox"></i>
          <div className="mail-empty__title">Mail bulunamadı</div>
          <div>Seçili filtrelerle eşleşen mail yok.</div>
        </div>
      ) : (
        <div className="mail-list">
          {mails.map((mail) => {
            const status = STATUS_META[mail.status];
            return (
              <div key={mail.id} className={`mail-row${!mail.isRead ? " mail-row--unread" : ""}`} onClick={() => setSelectedMessageId(mail.id)}>
                <input
                  type="checkbox"
                  className="mail-row-checkbox"
                  checked={selectedIds.has(mail.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelect(mail.id)}
                />
                <div className="mail-row-main">
                  <div className="mail-row-top">
                    <span className="mail-row-from">{mail.from.name ?? mail.from.email}</span>
                    <span className="mail-row-date">{timeAgo(mail.receivedAt)}</span>
                  </div>
                  <div className="mail-row-subject">{mail.subject || "(Konu yok)"}</div>
                  <div className="mail-row-snippet">{mail.snippet}</div>
                  <div className="mail-row-badges">
                    <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "2px 9px", borderRadius: 20, background: status.bg, color: status.color, border: `1px solid ${status.border}` }}>
                      {status.label}
                    </span>
                    {mail.hasAttachments && (
                      <span style={{ fontSize: "0.68rem", fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "#f1f5f9", color: "#475569" }}>
                        <i className="bi bi-paperclip"></i> {mail.attachmentCount}
                      </span>
                    )}
                    {mail.threadMessageCount > 1 && (
                      <span style={{ fontSize: "0.68rem", fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "#f1f5f9", color: "#475569" }}>
                        ({mail.threadMessageCount})
                      </span>
                    )}
                    {mail.isRegisteredUser && (
                      <span style={{ fontSize: "0.68rem", fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "#f5f3ff", color: "#7c3aed" }}>
                        <i className="bi bi-person-check-fill"></i> Kayıtlı
                      </span>
                    )}
                    {mail.ticket && (
                      <span style={{ fontSize: "0.68rem", fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "#f0fdf4", color: "#166534" }}>
                        <i className="bi bi-hash"></i>{mail.ticket.ticketNo}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {paging && paging.totalPages > 1 && (
        <div className="mail-pagination">
          <button className="mail-page-btn" disabled={pageIndex <= 0} onClick={() => goToPage(pageIndex - 1)}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <span className="mail-page-info">{pageIndex + 1} / {paging.totalPages}</span>
          <button className="mail-page-btn" disabled={pageIndex + 1 >= paging.totalPages} onClick={() => goToPage(pageIndex + 1)}>
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
      )}

      {selectedMessageId && (
        <MailDetailPanel
          messageId={selectedMessageId}
          onClose={() => setSelectedMessageId(null)}
          onChanged={refreshAll}
          isMobile={isMobile}
        />
      )}

      {showFilterRules && (
        <MailFilterRulesModal onClose={() => setShowFilterRules(false)} isMobile={isMobile} />
      )}
    </div>
  );
}
